import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CTYPES = ["Licence", "Ticket", "Induction", "Medical", "VOC"];
const inp = "w-full h-8 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";

export function complianceStatus(w) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let status = null;
  const expired = [];
  for (const c of (w?.compliances || [])) {
    if (!c.expiry_date) continue;
    const days = Math.floor((new Date(c.expiry_date) - today) / 86400000);
    if (days < 0) { expired.push(c); status = "expired"; }
    else if (days <= 30 && status !== "expired") status = "expiring";
  }
  return { status, expired };
}

export function ComplianceBadge({ worker }) {
  const { status } = complianceStatus(worker);
  if (!status) return (worker?.compliances || []).length
    ? <span className="inline-flex items-center gap-1.5 text-cell text-ink-secondary" data-testid={`compliance-ok-${worker.id}`}><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> OK</span>
    : <span className="text-ink-disabled">—</span>;
  return status === "expired"
    ? <span className="px-1.5 py-0.5 text-pill font-bold uppercase rounded-[4px] bg-red-50 text-red-700" data-testid={`compliance-expired-${worker.id}`}>EXPIRED</span>
    : <span className="px-1.5 py-0.5 text-pill font-bold uppercase rounded-[4px] bg-amber-50 text-amber-800" data-testid={`compliance-expiring-${worker.id}`}>EXPIRING</span>;
}

function rowLevel(c) {
  if (!c.expiry_date) return null;
  const days = Math.floor((new Date(c.expiry_date) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  return days < 0 ? "expired" : days <= 30 ? "expiring" : "ok";
}

export default function ComplianceTab({ form, setForm }) {
  const { isWriter } = useAuth();
  const items = form.compliances || [];
  const fileRef = useRef(null);
  const pendingIdx = useRef(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const setItem = (i, k, v) => setForm((s) => ({ ...s, compliances: (s.compliances || []).map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const add = () => setForm((s) => ({ ...s, compliances: [...(s.compliances || []), { ctype: "Ticket", name: "", ref_no: "", issue_date: "", expiry_date: "", notes: "" }] }));
  const remove = (i) => setForm((s) => ({ ...s, compliances: (s.compliances || []).filter((_, j) => j !== i) }));

  const startUpload = (i) => { pendingIdx.current = i; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const i = pendingIdx.current;
    if (!file || i == null) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/compliance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, compliances: (s.compliances || []).map((x, j) => j === i ? { ...x, doc_file_id: data.file_id, doc_filename: data.filename } : x) }));
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    setUploadingIdx(null);
  };
  const viewDoc = (c) => window.open(`${api.defaults.baseURL}/compliance/file/${c.doc_file_id}`, "_blank");

  return (
    <div className="p-5 flex-1 overflow-auto space-y-3" data-testid="compliance-tab">
      <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} data-testid="compliance-tab-file-input" />
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Licences, tickets, inductions & medicals — expiry dates drive dashboard and allocation warnings.</p>
        <button onClick={add} data-testid="compliance-add-btn"
          className="h-8 px-3 bg-equipr-yellow text-equipr-black text-xs font-bold rounded-none hover:bg-equipr-yellow-dark shrink-0">+ Add item</button>
      </div>
      {items.length === 0 && <p className="text-sm text-gray-400 py-6 text-center" data-testid="compliance-empty">No compliance records yet.</p>}
      {items.map((c, i) => {
        const lvl = rowLevel(c);
        return (
          <div key={c.id || i} data-testid={`compliance-row-${i}`}
            className={`border p-3 grid grid-cols-6 gap-2 ${lvl === "expired" ? "border-red-300 bg-red-50/50" : lvl === "expiring" ? "border-amber-300 bg-amber-50/50" : "border-gray-200"}`}>
            <div><label className="text-[10px] font-semibold text-gray-500 block">Type</label>
              <select className={inp} value={c.ctype} onChange={(e) => setItem(i, "ctype", e.target.value)} data-testid={`compliance-type-${i}`}>
                {CTYPES.map((t) => <option key={t}>{t}</option>)}
              </select></div>
            <div className="col-span-2"><label className="text-[10px] font-semibold text-gray-500 block">Name *</label>
              <input className={inp} value={c.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="e.g. HR Licence" data-testid={`compliance-name-${i}`} /></div>
            <div><label className="text-[10px] font-semibold text-gray-500 block">Ref #</label>
              <input className={inp} value={c.ref_no || ""} onChange={(e) => setItem(i, "ref_no", e.target.value)} data-testid={`compliance-ref-${i}`} /></div>
            <div><label className="text-[10px] font-semibold text-gray-500 block">Issued</label>
              <input type="date" className={inp} value={c.issue_date || ""} onChange={(e) => setItem(i, "issue_date", e.target.value)} /></div>
            <div><label className="text-[10px] font-semibold text-gray-500 block">Expiry
              {lvl === "expired" && <span className="ml-1 text-red-600 font-bold">EXPIRED</span>}
              {lvl === "expiring" && <span className="ml-1 text-amber-600 font-bold">SOON</span>}</label>
              <div className="flex gap-1">
                <input type="date" className={inp} value={c.expiry_date || ""} onChange={(e) => setItem(i, "expiry_date", e.target.value)} data-testid={`compliance-expiry-${i}`} />
                <button onClick={() => remove(i)} data-testid={`compliance-delete-${i}`} className="text-gray-400 hover:text-red-600 px-1">×</button>
              </div></div>
            <div className="col-span-6 flex items-center gap-3 text-xs">
              {c.doc_file_id ? (
                <button onClick={() => viewDoc(c)} data-testid={`compliance-tab-view-${i}`} className="text-accent-ink font-bold hover:underline">View document ({c.doc_filename || "file"})</button>
              ) : <span className="text-gray-400">No card photo/document attached</span>}
              {isWriter && (
              <button onClick={() => startUpload(i)} disabled={uploadingIdx === i} data-testid={`compliance-tab-upload-${i}`}
                className="text-gray-600 font-semibold hover:text-equipr-black disabled:opacity-50">
                {uploadingIdx === i ? "Uploading…" : c.doc_file_id ? "Replace" : "Upload photo / PDF"}
              </button>
              )}
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-gray-400">Remember to Save on the Details tab footer to persist changes.</p>
    </div>
  );
}
